import {useApiManager} from "../../../../components/utils/api";
import {
	BillAnnualSummary,
	BillDiscountRestControllerApiFactory,
	BillRestControllerApiFactory,
	DiscountSummary,
	PayrollAnnualSummary,
	PayrollRestControllerApiFactory,
	UserAuthItemTypeEnum
} from "arteco-api-client-ts";
import {useHasAnyRole} from "../../../../components/utils/auth";
import React, {useEffect, useState} from "react";
import {Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableRow, TextField, Typography} from "@mui/material";
import {numberToString} from "../../../../components/utils/numbers";
import {EmptyState, Panel, tableContainerSx, tableSx} from "../../../../components/kit";

const num = {fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"} as const;

export const BillHomePage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(BillDiscountRestControllerApiFactory);
	const billCtrl = apiManager.factory(BillRestControllerApiFactory);
	const payrollCtrl = apiManager.factory(PayrollRestControllerApiFactory);

	const [costsSummary, setCostsSummary] = useState<PayrollAnnualSummary | undefined>(undefined);
	const [discountSummary, setDiscountSummary] = useState<DiscountSummary | undefined>(undefined);
	const [annualSummary, setAnnualSummary] = useState<BillAnnualSummary | undefined>(undefined);
	const [year, setYear] = useState<number>(new Date().getFullYear());
	const [showPersonDetails, setShowPersonDetails] = useState<boolean>(false);
	// Las nóminas son de RR. HH.: facturación sola no las ve (#14079).
	const seesPayroll = useHasAnyRole(UserAuthItemTypeEnum.ADMIN, UserAuthItemTypeEnum.ADMIN_EMPLOYEE);

	useEffect(() => {
		apiManager.execute(ctrl.getSummaryBillDiscount(year))
		.then(resp => {
			setDiscountSummary(resp)
		})

		apiManager.execute(billCtrl.annualSummaryBill(year))
		.then(resp => {
			setAnnualSummary(resp)
		})

		if (seesPayroll) {
			apiManager.execute(payrollCtrl.annualSummaryPayroll(year))
			.then(resp => {
				setCostsSummary(resp)
			})
		}

	}, [year]);

	const billed = annualSummary?.lines?.reduce((acc, curr) => acc + (curr.netAmount || 0), 0) || 0;
	const costs = costsSummary?.lines?.reduce((acc, curr) => acc + (curr.companyCost || 0), 0) || 0;
	// #13813: el total ya incluye a quien no se evalúa; el subtotal es la cifra de antes
	const costsEvaluated = costsSummary?.lines?.filter(l => !l.skipEvaluation)
		.reduce((acc, curr) => acc + (curr.companyCost || 0), 0) || 0;

	return (
		<Panel title="Descuentos, facturación y costes del año"
			   subtitle="Facturación por mes de servicio y nóminas por su periodo, los dos hasta el último mes cerrado. Recuerda marcar «saltar descuento» en las facturas a las que se aplica el rápel."
			   action={<TextField type={"number"} size={"small"} label={"Año"} value={year} sx={{width: 110}}
								  onChange={e => setYear(parseInt(e.target.value))}/>}>
			<Box sx={{display: "grid", gap: 2, gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "repeat(3, minmax(0, 1fr))"}, alignItems: "start"}}>
				<Box sx={{minWidth: 0}}>
					<Typography variant="body2" fontWeight={700} gutterBottom>Descuentos por cliente</Typography>
					{!discountSummary?.lines?.length ? (
						<EmptyState title="Ningún descuento este año"/>
					) : (
						<Stack spacing={1}>
							{discountSummary.lines.map(ds => (
								<Box key={ds.customerShortName} sx={{p: 1.5, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "divider"}}>
									<Typography variant="body2" fontWeight={700}>{ds.customerShortName}</Typography>
									<Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5, mt: 0.5}}>
										<Typography variant="caption" color="text.secondary">Facturas</Typography>
										<Typography variant="caption" textAlign="right" sx={num}>{ds.billNum}</Typography>
										<Typography variant="caption" color="text.secondary">Importe sin IVA</Typography>
										<Typography variant="caption" textAlign="right" sx={num}>{numberToString(ds.totalAmount)} €</Typography>
										<Typography variant="caption" color="text.secondary">Tipo</Typography>
										<Typography variant="caption" textAlign="right" sx={num}>{ds.discountPct} %</Typography>
										<Typography variant="caption" color="text.secondary">Ahorro</Typography>
										<Typography variant="caption" textAlign="right" fontWeight={700} sx={num}>{numberToString(ds.discountSavings || 0)} €</Typography>
									</Box>
								</Box>
							))}
						</Stack>
					)}
				</Box>

				<Box sx={{minWidth: 0}}>
					<Typography variant="body2" fontWeight={700} gutterBottom>Facturación anual acumulada (sin IVA)</Typography>
					<TableContainer sx={tableContainerSx}>
						<Table size="small" sx={{...tableSx, tableLayout: "auto"}}>
							<TableBody>
								{annualSummary?.lines?.map(ds => (
									<TableRow key={"bl-" + ds.customerId}>
										<TableCell>{ds.customerName}</TableCell>
										<TableCell align="right" sx={num}>{numberToString(ds.netAmount)} €</TableCell>
									</TableRow>
								))}
							</TableBody>
							<TableFooter sx={{"& td": {fontWeight: 700, color: "text.primary", fontSize: 14, bgcolor: "grey.50"}}}>
								<TableRow>
									<TableCell>Total</TableCell>
									<TableCell align="right" sx={num}>{numberToString(billed)} €</TableCell>
								</TableRow>
							</TableFooter>
						</Table>
					</TableContainer>
				</Box>

				{!seesPayroll ? (
					<Box sx={{minWidth: 0}}>
						<Typography variant="body2" fontWeight={700} gutterBottom>Costes anuales de empresa</Typography>
						<Typography variant="body2" color="text.secondary">Salen de las nóminas, que solo ve RR. HH.</Typography>
					</Box>
				) : (
				<Box sx={{minWidth: 0}}>
					<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 0.5}}>
						<Typography variant="body2" fontWeight={700}>Costes anuales de empresa</Typography>
						<Button size="small" onClick={() => setShowPersonDetails(!showPersonDetails)}>
							{showPersonDetails ? "Agrupar" : "Ver por persona"}
						</Button>
					</Stack>
					<TableContainer sx={tableContainerSx}>
						<Table size="small" sx={{...tableSx, tableLayout: "auto"}}>
							<TableBody>
								{showPersonDetails && costsSummary?.lines?.map(ds => (
									<TableRow key={"bl-" + ds.personId}>
										<TableCell>{ds.personName}</TableCell>
										<TableCell align="right" sx={num}>{numberToString(ds.companyCost)} €</TableCell>
									</TableRow>
								))}
								{!showPersonDetails && (
									<TableRow><TableCell colSpan={2} sx={{color: "text.secondary"}}>Líneas agrupadas</TableCell></TableRow>
								)}
							</TableBody>
							<TableFooter sx={{"& td": {color: "text.primary", fontSize: 14, bgcolor: "grey.50"}}}>
								<TableRow>
									<TableCell sx={{fontWeight: 700}}>Total</TableCell>
									<TableCell align="right" sx={{...num, fontWeight: 700}}>{numberToString(costs)} €</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>Sin personas fuera de evaluación</TableCell>
									<TableCell align="right" sx={num}>{numberToString(costsEvaluated)} €</TableCell>
								</TableRow>
							</TableFooter>
						</Table>
					</TableContainer>
				</Box>
				)}
			</Box>
		</Panel>
	);
};
